import { PageHero } from '@components/common';
import { getSectorCard } from '@data/sectorCards';
import { CALENDLY_URL } from '@constants';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, HeartPulse, Database, Lock } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function HealthcarePage() {
  const sectorSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Fractional CISO for Healthcare',
    description:
      'Protecting patient data and medical infrastructure in an increasingly digital NHS',
    url: 'https://thecisonetwork.com/sectors/healthcare',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
    areaServed: {
      '@type': 'DefinedTerm',
      name: 'Healthcare',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(sectorSchema)}</script>
      <PageHero
        title="Fractional CISO for Healthcare"
        subtitle="Protecting patient data and medical infrastructure in an increasingly digital NHS"
        eyebrow="Healthcare"
        backgroundImage={getSectorCard('healthcare')?.image}
        backgroundAlt={getSectorCard('healthcare')?.imageAlt}
        seoTitle="Fractional CISO Healthcare | The CISO Network"
        seoDescription="Expert fractional CISO services for healthcare organisations. Protect patient data, ensure NHS compliance, and prevent devastating medical data breaches."
      />

      {/* Why Healthcare Needs CISOs */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Cybersecurity Leadership for Healthcare
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Healthcare organisations hold some of the most sensitive and valuable data
                  imaginable - comprehensive medical records, mental health histories, genetic
                  information, and payment details. This data commands premium prices on the dark
                  web, making hospitals, clinics, and healthtech companies persistent targets for
                  cybercriminals.
                </p>
                <p>
                  The digital transformation of healthcare has created an expanded attack surface.
                  Electronic patient records, connected medical devices, telemedicine platforms, and
                  cloud-based health apps all introduce vulnerabilities. NHS trusts face particular
                  challenges with legacy systems, complex supply chains, and the life-or-death
                  nature of their operations.
                </p>
                <p>
                  Regulatory requirements are stringent and growing.{' '}
                  <Link to="/services/compliance" className="text-secondary hover:underline">
                    NHS Digital's Data Security and Protection Toolkit (DSPT)
                  </Link>{' '}
                  is mandatory, GDPR penalties for health data breaches can reach 4% of turnover,
                  and the CQC now inspects cybersecurity as part of its assessments. Yet many
                  healthcare organisations lack the internal expertise to navigate this landscape
                  effectively.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <HeartPulse className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Patient Safety Focus
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Healthcare breaches aren't just data incidents - they can directly impact
                      patient care. Our CISOs understand that in healthcare, cybersecurity is
                      patient safety.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Database className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      NHS & HealthTech Expertise
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      From NHS trusts to digital health startups, our CISOs bring sector-specific
                      knowledge of healthcare systems, regulations, and the unique challenges you
                      face.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Historical Security Cases */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Healthcare's Costly Breaches
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Real incidents where healthcare organisations suffered breaches that a CISO could have
              prevented
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: AlertTriangle,
                title: 'NHS WannaCry (2017)',
                impact: '19,000 appointments cancelled, £92M cost',
                description:
                  'The ransomware attack exploited unpatched Windows XP systems across 80 NHS trusts. A CISO would have established patch management, ensured critical security updates were deployed, and maintained incident response capabilities to isolate affected systems quickly.',
              },
              {
                icon: AlertTriangle,
                title: 'Bupa (2017)',
                impact: '547,000 customer records stolen',
                description:
                  'An employee copied customer data and attempted to sell it on the dark web. A CISO would have implemented data loss prevention (DLP) systems, user activity monitoring, and strict access controls that could have detected and prevented the exfiltration.',
              },
              {
                icon: AlertTriangle,
                title: 'NHS Surrey (2012)',
                impact: '£200,000 ICO fine, 3,000+ patient records exposed',
                description:
                  'Patient records were found on hard drives sold on eBay after a third-party disposal company failed to destroy them properly. A CISO would have implemented secure asset disposal procedures, verified destruction certificates, and audited third-party vendors handling sensitive data.',
              },
              {
                icon: AlertTriangle,
                title: 'Synnovis Ransomware (June 2024)',
                impact: '800+ operations cancelled, 10,000+ appointments affected',
                description:
                  'A Russian cyber gang attacked this pathology services provider, causing massive disruption to London hospitals. A CISO would have ensured robust backup and recovery systems, segmentation of critical services, and incident response plans to minimise patient impact.',
              },
              {
                icon: AlertTriangle,
                title: 'Advanced Software (2022)',
                impact: '£3.07M ICO fine, 79,000+ records, NHS 111 disrupted',
                description:
                  'Hackers accessed systems via an account without multi-factor authentication, disrupting NHS 111 and care systems. The ICO issued its first fine against a data processor. A CISO would have ensured complete MFA coverage, vulnerability scanning, and timely patching.',
              },
              {
                icon: AlertTriangle,
                title: '56 Dean Street Clinic (2015)',
                impact: '£180,000 ICO fine, 781 HIV patients exposed',
                description:
                  "An email newsletter was sent with all patient addresses visible in the 'To' field rather than 'Bcc', exposing recipients' HIV status. A CISO would implement email safeguards, mandatory BCC checks, and staff training on handling sensitive health data.",
              },
            ].map((case_, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-border">
                <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                  <case_.icon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {case_.title}
                </h3>
                <p className="text-sm font-medium text-red-600 mb-3">{case_.impact}</p>
                <p className="text-muted-foreground text-sm">{case_.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Risks */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Critical Risks in Healthcare
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              The threats targeting patient data and medical systems
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Ransomware',
                description:
                  'Healthcare is the #1 target for ransomware because attackers know hospitals will pay to restore patient-critical systems.',
              },
              {
                icon: Database,
                title: 'Medical Device Hacking',
                description:
                  'Connected devices - pacemakers, insulin pumps, MRI machines - often run outdated software and lack security updates.',
              },
              {
                icon: Lock,
                title: 'Insider Threats',
                description:
                  'Staff with legitimate access can abuse patient data, whether for financial gain, curiosity, or negligence.',
              },
              {
                icon: HeartPulse,
                title: 'Operational Disruption',
                description:
                  'Unlike other sectors, downtime in healthcare can literally cost lives, making healthcare uniquely vulnerable.',
              },
            ].map((risk, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <risk.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {risk.title}
                </h3>
                <p className="text-muted-foreground text-sm">{risk.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO for Healthcare - FAQs"
        subtitle="Common questions from NHS trusts, healthtech, and pharmaceutical organisations"
        items={[
          {
            id: 'healthcare-1',
            question: 'How does a fractional CISO work with healthcare organisations?',
            answer:
              'Our CISOs work with NHS trusts, private providers, healthtech startups, and pharmaceutical companies. They understand the unique pressures of healthcare environments — from patient data protection to medical device security — and provide practical guidance that works within clinical and operational constraints.',
          },
          {
            id: 'healthcare-2',
            question: 'What regulations does a healthcare CISO need to manage?',
            answer:
              'Healthcare CISOs must navigate GDPR for patient data, NHS Digital standards including DSPT, MHRA requirements for medical devices, and the NHS Cyber Alert system. Our CISOs have direct NHS and private healthcare security experience.',
          },
          {
            id: 'healthcare-3',
            question: 'How does a fractional CISO help with NHS DSPT compliance?',
            answer:
              'Our CISOs guide NHS organisations through the Data Security and Protection Toolkit assessment, identify evidence gaps, implement missing controls, and ensure submission readiness. We have helped trusts achieve DSPT compliance within 8-12 weeks from initial assessment.',
          },
          {
            id: 'healthcare-4',
            question: 'Can a fractional CISO support healthtech startups?',
            answer:
              'Yes. Healthtech startups face the dual challenge of medical regulation and cybersecurity. Our CISOs help these companies achieve MHRA compliance, secure patient data platforms, and meet the security requirements of NHS procurement frameworks.',
          },
          {
            id: 'healthcare-5',
            question: 'What is the first priority for a healthcare fractional CISO?',
            answer:
              'Patient data protection is always the first priority. Our CISOs begin by mapping how patient data flows through your systems, who has access, and what controls protect it. This forms the foundation for a security programme that satisfies regulators and protects the patients you serve.',
          },
        ]}
      />

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Protect Your Patients and Your Organisation
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Healthcare cybersecurity isn't optional - it's a matter of patient trust and safety. Our
            fractional CISOs bring healthcare expertise to secure your organisation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
